import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAgentExecution } from "@/contexts/AgentExecutionContext";
import {
  Bot,
  CheckCircle2,
  Loader2,
  AlertTriangle,
  XCircle,
  ExternalLink,
  Sparkles,
  StopCircle,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface AgentProgressModalProps {
  isOpen: boolean;
  onClose: () => void;
  runId?: string | null;
  getAgentLabel: (agentType?: string) => string;
  getAgentPath: (agentType?: string) => string;
}

export const AgentProgressModal: React.FC<AgentProgressModalProps> = ({
  isOpen,
  onClose,
  runId,
  getAgentLabel,
  getAgentPath,
}) => {
  const navigate = useNavigate();
  const { runs, cancelRun } = useAgentExecution();

  const allRunsList = Object.values(runs);
  const activeRun = (runId && runs[runId])
    ? runs[runId]
    : allRunsList.find(r => r.status === "loading" || r.status === "streaming") || allRunsList[allRunsList.length - 1];

  if (!activeRun) {
    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="sm:max-w-lg rounded-2xl p-6 bg-white dark:bg-slate-900">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold flex items-center gap-2 text-slate-900 dark:text-white">
              <Bot className="h-5 w-5 text-blue-600" /> Progreso del Agente
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500 dark:text-slate-400">
              No se registra ninguna ejecución en esta sesión. Puedes iniciar un análisis desde el menú de Agentes.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-6">
            <Button variant="outline" onClick={onClose} className="rounded-xl">
              Cerrar
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl"
              onClick={() => {
                onClose();
                navigate("/dashboard/agents");
              }}
            >
              Ir a Mis Agentes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const steps = activeRun.traceSteps || [];
  const activeStepIndex = steps.findIndex(s => s.status === "running");
  const currentStep = activeStepIndex !== -1 ? steps[activeStepIndex] : steps[steps.length - 1];

  const doneCount = steps.filter(s => s.status === "done").length;
  const totalSteps = Math.max(steps.length, 1);
  
  let progressPercent = 0;
  if (activeRun.status === "completed") {
    progressPercent = 100;
  } else if (steps.length > 0) {
    progressPercent = Math.min(95, Math.max(15, Math.round(((doneCount + (activeStepIndex !== -1 ? 0.5 : 0)) / totalSteps) * 100)));
  } else {
    progressPercent = 25;
  }

  const handleNavigate = () => {
    onClose();
    const path = getAgentPath(activeRun.agentType);
    const query = activeRun.agentType === 'legal-chatbot' ? `?chat_id=${activeRun.id}` : '';
    navigate(path + query);
  };

  const handleCancel = () => {
    cancelRun(activeRun.id);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-xl rounded-3xl p-0 overflow-hidden border-0 shadow-2xl bg-white dark:bg-slate-900">
        {/* Header decoration bar */}
        <div className="h-2 w-full bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600" />

        <div className="p-6 space-y-6">
          {/* Header Title & Status */}
          <DialogHeader className="space-y-1">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-md shadow-blue-500/20 text-white">
                  <Bot className="h-5 w-5" />
                </div>
                <div>
                  <DialogTitle className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                    {getAgentLabel(activeRun.agentType)}
                    <span className={cn(
                      "inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border",
                      activeRun.status === "completed"
                        ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 border-emerald-200"
                        : activeRun.status === "error" || activeRun.status === "rejected"
                        ? "bg-red-50 text-red-700 dark:bg-red-950/60 dark:text-red-300 border-red-200"
                        : "bg-blue-50 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300 border-blue-200"
                    )}>
                      {(activeRun.status === "loading" || activeRun.status === "streaming") && (
                        <span className="relative flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
                        </span>
                      )}
                      {activeRun.status === "completed" ? "Completado" : activeRun.status === "streaming" ? "Generando respuesta" : activeRun.status === "error" ? "Error" : "Ejecutando..."}
                    </span>
                  </DialogTitle>
                  <DialogDescription className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    ID de sesión: <code className="bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded text-[11px] text-slate-700 dark:text-slate-300">{activeRun.id.slice(0, 18)}...</code>
                  </DialogDescription>
                </div>
              </div>
            </div>
          </DialogHeader>

          {/* Current Highlighted Activity Card */}
          <div className={cn(
            "p-4 rounded-2xl border shadow-sm relative overflow-hidden transition-all duration-300",
            activeRun.status === "completed"
              ? "bg-gradient-to-br from-emerald-50/90 via-teal-50/50 to-slate-50 border-emerald-200/90 dark:from-slate-800 dark:via-emerald-950/30 dark:to-slate-900 dark:border-emerald-800/60"
              : activeRun.status === "error" || activeRun.status === "rejected"
              ? "bg-gradient-to-br from-red-50/90 via-rose-50/50 to-slate-50 border-red-200/90 dark:from-slate-800 dark:via-red-950/30 dark:to-slate-900 dark:border-red-800/60"
              : "bg-gradient-to-br from-blue-50/80 via-indigo-50/40 to-slate-50 border-blue-100/80 dark:from-slate-800/80 dark:via-blue-950/20 dark:to-slate-900 dark:border-blue-900/40"
          )}>
            <div className="flex items-start gap-3.5">
              <div className="text-2xl bg-white dark:bg-slate-800 p-2 rounded-xl shadow-sm border border-slate-100 dark:border-slate-700 flex items-center justify-center shrink-0">
                {activeRun.status === "completed" ? "🎉" : (currentStep?.emoji || "⚡")}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className={cn(
                    "text-[10px] font-black uppercase tracking-wider flex items-center gap-1",
                    activeRun.status === "completed" ? "text-emerald-700 dark:text-emerald-400" : "text-blue-600 dark:text-blue-400"
                  )}>
                    <Sparkles className="h-3 w-3" /> {activeRun.status === "completed" ? "¡Resultado Listo!" : "Pasó Actual"}
                  </span>
                </div>
                <h4 className="text-sm font-bold text-slate-900 dark:text-white leading-snug">
                  {activeRun.status === "completed"
                    ? "¡Análisis e informe completados con éxito!"
                    : currentStep
                    ? (typeof currentStep.message === "object" ? JSON.stringify(currentStep.message) : String(currentStep.message || ""))
                    : "Iniciando análisis del agente..."}
                </h4>
                <p className="text-xs text-slate-600 dark:text-slate-300 mt-1 leading-relaxed">
                  {activeRun.status === "completed"
                    ? "Haz clic en 'Ver Resultado Completo' para abrir el documento, análisis o respuesta generada por el agente."
                    : currentStep?.detail || "Conectando servicios y procesando datos..."}
                </p>
              </div>
              {(activeRun.status === "loading" || activeRun.status === "streaming" || currentStep?.status === "running") && (
                <Loader2 className="h-5 w-5 animate-spin text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
              )}
              {activeRun.status === "completed" && (
                <CheckCircle2 className="h-6 w-6 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
              )}
            </div>
          </div>


          {/* Progress Bar Container */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="font-semibold text-slate-700 dark:text-slate-300">
                Progreso del Agente
              </span>
              <span className={cn("font-bold", activeRun.status === "completed" ? "text-emerald-600 dark:text-emerald-400" : "text-blue-600 dark:text-blue-400")}>
                {progressPercent}%
              </span>
            </div>
            <div className="h-2.5 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden p-0.5 border border-slate-200/50 dark:border-slate-700/50">
              <div
                className={cn(
                  "h-full rounded-full transition-all duration-500 ease-out shadow-sm",
                  activeRun.status === "completed"
                    ? "bg-gradient-to-r from-emerald-500 to-teal-600"
                    : "bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-600"
                )}
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>

          {/* Trace Steps Timeline */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                Línea de tiempo del proceso ({doneCount}/{totalSteps})
              </h4>
            </div>

            <div className="max-h-[220px] overflow-y-auto space-y-2.5 pr-1.5 scrollbar-thin scrollbar-thumb-slate-200 dark:scrollbar-thumb-slate-700">
              {steps.length === 0 ? (
                <div className="py-6 text-center text-xs text-slate-400 dark:text-slate-500 flex flex-col items-center gap-2">
                  <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
                  <span>Inicializando flujo de trabajo...</span>
                </div>
              ) : (
                steps.map((step, idx) => {
                  const isDone = step.status === "done";
                  const isRunning = step.status === "running";
                  const isError = step.status === "error";
                  const isWarn = step.status === "warn";

                  return (
                    <div
                      key={idx}
                      className={cn(
                        "flex items-start gap-3 p-3 rounded-xl border text-xs transition-all duration-200",
                        isRunning
                          ? "bg-blue-50/90 border-blue-200 dark:bg-blue-950/40 dark:border-blue-800/80 shadow-sm"
                          : isDone
                          ? "bg-slate-50/60 border-slate-100 dark:bg-slate-800/40 dark:border-slate-800/60 opacity-85"
                          : isError
                          ? "bg-red-50/80 border-red-200 dark:bg-red-950/40 dark:border-red-900/60 text-red-700 dark:text-red-300"
                          : "bg-amber-50/80 border-amber-200 dark:bg-amber-950/40 dark:border-amber-900/60"
                      )}
                    >
                      <div className="shrink-0 mt-0.5">
                        {isDone && <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
                        {isRunning && <Loader2 className="h-4 w-4 animate-spin text-blue-600 dark:text-blue-400" />}
                        {isError && <XCircle className="h-4 w-4 text-red-500" />}
                        {isWarn && <AlertTriangle className="h-4 w-4 text-amber-500" />}
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className={cn(
                          "font-semibold leading-tight",
                          isRunning ? "text-blue-900 dark:text-blue-100 font-bold" : "text-slate-800 dark:text-slate-200"
                        )}>
                          {step.emoji && <span className="mr-1.5">{step.emoji}</span>}
                          {typeof step.message === "object" ? JSON.stringify(step.message) : String(step.message || "")}
                        </p>
                        {step.detail && (
                          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">
                            {step.detail}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Footer Actions */}
          <div className="pt-2 flex flex-col sm:flex-row items-center justify-between gap-2 border-t border-slate-100 dark:border-slate-800">
            {activeRun.status !== "completed" ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCancel}
                className="w-full sm:w-auto text-xs font-semibold text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/50 rounded-xl h-9"
              >
                <StopCircle className="h-3.5 w-3.5 mr-1.5" /> Cancelar Agente
              </Button>
            ) : <div />}

            <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={onClose}
                className="flex-1 sm:flex-none text-xs font-semibold rounded-xl h-9"
              >
                Cerrar
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleNavigate}
                className={cn(
                  "flex-1 sm:flex-none text-xs font-bold text-white shadow-md rounded-xl h-9 px-4",
                  activeRun.status === "completed"
                    ? "bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 shadow-emerald-500/20"
                    : "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-blue-500/20"
                )}
              >
                {activeRun.status === "completed" ? "Ver Resultado Completo" : "Ir a la Pantalla"} <ExternalLink className="h-3.5 w-3.5 ml-1.5" />
              </Button>
            </div>
          </div>

        </div>
      </DialogContent>
    </Dialog>
  );
};
